import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  memo,
  useCallback,
} from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  keyframes,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import { getHeader, getVariableNames, toastError } from "../../utils/util";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";
import { MdOutlineSmartToy } from "react-icons/md";
import { useDialog } from "../../context/DialogContext";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
const pulseBorder = keyframes`
  0% {
    box-shadow: 0 0 0 0px rgba(10, 50, 150, 0.6);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(10, 50, 150, 0);
  }
  100% {
    box-shadow: 0 0 0 0px rgba(10, 50, 150, 0);
  }
`;
function TemplateBody({
  variableType = "Number",
  varLimitCount = 10,
  charLimit = 1024,
  value: controlledValue,
  params: controlledParams,
  onChange,
}) {
  const { showInputDialog, hideInputDialog } = useDialog();
  const [internalValue, setInternalValue] = useState("");
  const [aiContentLoading, setAiContentLoading] = useState(false);
  const [internalParams, setInternalParams] = useState({});
  const isControlledValue = controlledValue !== undefined;
  const isControlledParams = controlledParams !== undefined;
  const value = isControlledValue ? controlledValue : internalValue;
  const params = isControlledParams ? controlledParams : internalParams;
  const setValue = isControlledValue ? () => {} : setInternalValue;
  const setParams = isControlledParams ? () => {} : setInternalParams;
  const safeCharLimit = Math.max(10, charLimit);

  /* -------- tokens + keep params obj in sync -------- */
  const tokens = useMemo(
    () => getVariableNames(variableType, value),
    [variableType, value]
  );
  useEffect(() => {
    const next = { ...params };
    let changed = false;
    tokens.forEach((t) => {
      if (!(t in next)) {
        next[t] = "";
        changed = true;
      }
    });
    Object.keys(next).forEach((k) => {
      if (!tokens.includes(k)) {
        delete next[k];
        changed = true;
      }
    });
    if (changed) {
      setParams(next);
      onChange?.(value, next);
    }
  }, [tokens.join("|")]);

  /* -------- render-state helpers -------- */
  const isButtonDisabled =
    tokens.length >= varLimitCount || value.length >= safeCharLimit;

  /* -------- refs -------- */
  const textFieldRef = useRef(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  /* -------- push helper -------- */
  const pushValue = (nextVal, nextParams = params) => {
    if (isControlledValue && onChange) onChange(nextVal, nextParams);
    else setInternalValue(nextVal);
    if (!isControlledParams) setInternalParams(nextParams);
    else onChange?.(nextVal, nextParams);
  };

  /* -------- handlers -------- */
  const handleTextChange = (e) => {
    const next = e.target.value.slice(0, safeCharLimit);
    pushValue(next);
  };

  const insertSnippet = (snippet, cursorBack = 0) => {
    const input =
      textFieldRef.current?.querySelector("textarea") ||
      textFieldRef.current?.querySelector("input");
    if (!input) return;

    const scrollTop = input.scrollTop; // Capture current scroll position
    const cursorPos = input.selectionStart || value.length;
    const before = value.slice(0, cursorPos);
    const after = value.slice(cursorPos);
    const newText = `${before}${snippet}${after}`;

    if (newText.length > safeCharLimit) return;

    pushValue(newText);
    setTimeout(() => {
      input.focus();
      const newCursorPos = cursorPos + snippet.length - cursorBack;
      input.setSelectionRange(newCursorPos, newCursorPos);
      input.scrollTop = scrollTop; // Restore scroll position
    }, 0);
  };

  const fetchAiContent = useCallback(async (msg) => {
    try {
      setAiContentLoading(true);
      const response = await axios.post(
        `${API_ENDPOINT}/agent/generate-assistant`,
        { message: msg, type: "templateAssistant" },
        getHeader()
      );
      pushValue(response.data.template);
      toast.success("You content is ready!");
    } catch (error) {
      toastError(error);
    } finally {
      setAiContentLoading(false);
    }
  }, []);

  const handleFormatClick = (format) => {
    if (isButtonDisabled) return;
    let snippet = "";
    let cursorBack = 0;
    switch (format) {
      case "bold":
        snippet = "**";
        cursorBack = 1;
        break;
      case "strikethrough":
        snippet = "~~";
        cursorBack = 1;
        break;
      case "monospace":
        snippet = "```";
        cursorBack = 3;
        break;
      case "italic":
        snippet = "__";
        cursorBack = 1;
        break;
      default:
        return;
    }
    insertSnippet(snippet, cursorBack);
  };

  const addVariable = () => {
    if (isButtonDisabled) return;
    const token = variableType === "Name" ? "{{}}" : `{{${tokens.length + 1}}}`;
    const cursorBack = token === "{{}}" ? 2 : 0;
    insertSnippet(token, cursorBack);
  };
  const handleAiHelpClick = useCallback(() => {
    showInputDialog("Template Assistant", "Describe", {
      isInputType: "template",
      inputLabel: "Tell ai to generate ur content",
      onConfirm: (mode) => {
        fetchAiContent(mode);
        hideInputDialog();
      },
    });
  }, []);

  const handleEmojiSelect = (em) => {
    insertSnippet(em, 0);
    setEmojiAnchorEl(null);
  };

  const updateParam = (tok, txt) => {
    const next = { ...params, [tok]: txt };
    pushValue(value, next);
  };

  /* -------- helper text -------- */
  const len = value.length;
  const helper =
    `${len}/${safeCharLimit}` +
    (len === safeCharLimit
      ? " – limit reached"
      : len >= safeCharLimit - 10
      ? " – approaching"
      : "");

  /* ===================== JSX ===================== */
  return (
    <Box>
      <TextField
        fullWidth
        multiline
        rows={4}
        value={value}
        onChange={handleTextChange}
        inputProps={{ maxLength: safeCharLimit }}
        placeholder="Body Text"
        ref={textFieldRef}
        error={len === safeCharLimit}
        sx={{
          "& .MuiOutlinedInput-root": {
            animation: aiContentLoading ? `${pulseBorder} 2s infinite` : "none",
            transition: "all 0.3s ease",
          },
        }}
        helperText={
          <Typography
            variant="caption"
            color={
              len === safeCharLimit
                ? "error"
                : len >= safeCharLimit - 10
                ? "warning"
                : "textSecondary"
            }
          >
            {helper}
          </Typography>
        }
      />
      <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: -2 }}>
        <Tooltip title="Bold">
          <GlassButtonLoading
            loading={aiContentLoading}
            size="small"
            onClick={handleAiHelpClick}
            startIcon={<MdOutlineSmartToy fontSize="small" />}
          >
            AI
          </GlassButtonLoading>
        </Tooltip>
        <Tooltip title="Bold">
          <IconButton
            size="small"
            disabled={isButtonDisabled}
            onClick={() => handleFormatClick("bold")}
          >
            <FormatBoldIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough">
          <IconButton
            size="small"
            disabled={isButtonDisabled}
            onClick={() => handleFormatClick("strikethrough")}
          >
            <StrikethroughSIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Monospace">
          <IconButton
            size="small"
            disabled={isButtonDisabled}
            onClick={() => handleFormatClick("monospace")}
          >
            <CodeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton
            size="small"
            disabled={isButtonDisabled}
            onClick={() => handleFormatClick("italic")}
          >
            <FormatItalicIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Emoji">
          <IconButton
            size="small"
            disabled={isButtonDisabled}
            onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
          >
            <EmojiEmotionsIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={emojiAnchorEl}
          open={Boolean(emojiAnchorEl)}
          onClose={() => setEmojiAnchorEl(null)}
        >
          <Picker
            data={emojiData}
            onEmojiSelect={(emoji) => handleEmojiSelect(emoji.native)}
            maxFrequentRows={0}
            perLine={8}
            skinTonePosition="search"
            theme="auto" // follows system dark/light
            previewPosition="none"
          />
        </Menu>
        <Tooltip title="Add Variable">
          <GlassButton
            size="small"
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
            disabled={isButtonDisabled}
            onClick={addVariable}
          >
            Variable
          </GlassButton>
        </Tooltip>
      </Box>
      {/* parameter inputs */}
      {tokens.map((tok) => (
        <Stack
          key={tok}
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mt: 2 }}
        >
          <code>{`{{${tok}}}`}</code>
          <TextField
            size="small"
            fullWidth
            label={`Value`}
            value={params[tok] || ""}
            onChange={(e) => updateParam(tok, e.target.value)}
          />
        </Stack>
      ))}
    </Box>
  );
}

export default memo(TemplateBody);
